import { useEffect, useState } from "react";
import { SEO, getFAQSchema } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export default function FAQPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    apiFetch("/api/faq")
      .then((json) => {
        if (json.success) {
          setFaqs(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title="Perguntas Frequentes"
        description="Tire suas dúvidas sobre o ZWeb. Perguntas frequentes sobre preços, implantação, suporte, licenças e integrações."
        keywords={["faq", "perguntas frequentes", "dúvidas", "zweb", "suporte"]}
        canonicalUrl="/faq"
        structuredData={faqs.length > 0 ? getFAQSchema(faqs) : undefined}
      />
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/blog" className="text-sm font-medium hover:text-accent-600">Blog</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <h1 className="text-4xl font-bold">Perguntas Frequentes</h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Tire suas dúvidas sobre o ZWeb.
            </p>
          </div>

          <div className="mt-8">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pergunta..."
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
            />
          </div>

          {loading ? (
            <div className="mt-12 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
            </div>
          ) : filteredFaqs.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                {search ? "Nenhum resultado encontrado." : "Em breve publicaremos novas perguntas!"}
              </p>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              {filteredFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950"
                >
                  <button
                    className="flex w-full items-center justify-between p-6 text-left"
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                  >
                    <span className="font-semibold">{faq.question}</span>
                    <svg
                      className={`h-5 w-5 flex-shrink-0 transition-transform ${
                        openFaq === faq.id ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {openFaq === faq.id && (
                    <div className="px-6 pb-6 text-gray-600 dark:text-gray-400">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}